import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { alpha, Grid } from '@mui/system';
import { isEqual } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { SECURITY_ACTIONS } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import Confirm from '../components/Confirm';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { createGroupByOptions, getReportSortFormConfig } from '../components/forms/groupByConfigurations/groupByUtils';
import Loading from '../components/Loading';
import { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import type { Entity } from '../constants/customSortFieldConfigs';
import { FormContainer } from '../form/FormContainer';
import { FormProvider } from '../form/FormContext';
import FormItem from '../form/FormItem';
import FormPanel from '../form/FormPanel';
import FormWidget from '../form/FormWidget';
import Validator from '../form/Validator';
import GalleryCard from '../GalleryCard';
import I18n, { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { H } from '../Layout';
import { optimisticDeletePreference } from './reportApiUtils';
import ReportGalleryCard from './ReportGalleryCard';
import { mapPreferencesToTemplates, mapTemplateConfigToReportConfigurations } from './reportUtils';

export default function ReportTemplateStep({
    entity,
    preferenceEntityKey
}: {
    entity: Entity;
    preferenceEntityKey: string;
}) {
    const t = useI18n();
    const { preferences, refreshPreferences, preferencesLoaded, userProfile, activeOrganizationAccount } =
        useContext(UserContext);
    const { getStepData, setStepData, activeStep, setTemplateData, getTemplateData } = useStepper();
    const currentData = getStepData(activeStep);
    const templateData = getTemplateData(StepsIndexConstant.zero) || null;
    const [values, setValues] = useState(currentData || {});
    const [selectedId, setSelectedId] = useState<string | null>(currentData?.selectedId || null);
    const [visibleField, setVisibleField] = useState<boolean>(!!currentData?.selectedId);
    const [localTemplates, setLocalTemplates] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [resourceIdToDelete, setResourceIdToDelete] = useState<any | null>(null);

    const reportSortFormConfig = getReportSortFormConfig(entity);
    const reportGroupByKeys = reportSortFormConfig.resolveGroupByKeys(entity);
    const keyWorkspaceGroupByOptions = createGroupByOptions(reportGroupByKeys, t);

    const handleTemplateSelected = (resource) => {
        setSelectedId(resource.id);
        setVisibleField(true);
        const reportConfigurations = mapTemplateConfigToReportConfigurations(
            resource?.template?.templateConfig,
            keyWorkspaceGroupByOptions,
            entity
        );
        setValues((prev) => ({
            ...prev,
            selectedId: resource.id,
            reportTitle: resource.name,
            reportConfigurations
        }));

        setStepData(StepsIndexConstant.one, undefined);
        setStepData(StepsIndexConstant.two, undefined);
    };

    const validationRules = {
        reportTitle: [Validator.RULES.isRequired]
    };

    useEffect(() => {
        if (!isEqual(currentData, values)) {
            setStepData(activeStep, values);
        }
    }, [activeStep, currentData, setStepData, values]);

    useEffect(() => {
        if (preferencesLoaded) {
            const mapped = mapPreferencesToTemplates(preferences, preferenceEntityKey);
            setLocalTemplates(mapped);
        }
    }, [preferences, preferencesLoaded, preferenceEntityKey]);

    useEffect(() => {
        if (preferencesLoaded && localTemplates) {
            setTemplateData(StepsIndexConstant.zero, [...localTemplates]);
        }
    }, [preferencesLoaded, localTemplates, setTemplateData]);

    return (
        <FormProvider validationRules={validationRules} state={values} setState={setValues}>
            <FormPanel>
                <FormContainer
                    containerSx={{ display: 'flex', gap: 2 }}
                    showDivider
                    titleI18nToken="form.template.choose"
                >
                    <H gap={2}>
                        {!templateData ? (
                            <Loading />
                        ) : templateData?.length === 0 ? (
                            <I18n token="form.template.error.missing.message" />
                        ) : (
                            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4 }}>
                                {templateData?.map((resource, index) => (
                                    <Grid size={{ xs: 1, sm: 1, md: 1 }} key={`${index}_${resource.id}`}>
                                        <GalleryCard
                                            id={`${index}_${resource.id}`}
                                            item={resource}
                                            ItemRenderer={ReportGalleryCard}
                                            selectedId={selectedId}
                                            onDetails={() => handleTemplateSelected(resource)}
                                            itemName={resource.name}
                                            securedActions={[
                                                {
                                                    icon: Icons.Delete,
                                                    securityAction: SECURITY_ACTIONS.DELETE,
                                                    entityName: ENTITY_CONSTANTS.preference,
                                                    entitySecurity: resource.__raw?.security,
                                                    onClick: () => {
                                                        setResourceIdToDelete(resource.id);
                                                        setDeleteModalOpen(true);
                                                    }
                                                }
                                            ]}
                                            containerSx={(theme: Theme) => ({
                                                position: 'relative',
                                                border: `1px solid ${theme.palette.divider}`,
                                                outline:
                                                    selectedId === resource?.id
                                                        ? `2px solid ${alpha(theme.palette.primary.main, 0.7)}`
                                                        : 'none',
                                                outlineOffset: '-1px',
                                                '&::after':
                                                    selectedId === resource?.id
                                                        ? {
                                                              content: '""',
                                                              position: 'absolute',
                                                              inset: 0,
                                                              backgroundColor: alpha(
                                                                  theme.palette.primary.main,
                                                                  0.07
                                                              ),
                                                              borderRadius: 'inherit',
                                                              pointerEvents: 'none'
                                                          }
                                                        : {}
                                            })}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </H>
                </FormContainer>

                <FormContainer hidden={!visibleField}>
                    <FormItem>
                        <FormWidget name="reportTitle" label={<I18n token="report.field.title" />} />
                    </FormItem>
                </FormContainer>
            </FormPanel>
            {deleteModalOpen && resourceIdToDelete && (
                <Confirm
                    open={deleteModalOpen}
                    title={<I18n token={'delete.confirm.title'} />}
                    okLabelI18n="delete.confirm.title"
                    body={
                        <Typography>
                            <I18n token={'delete.confirm.message'} />
                        </Typography>
                    }
                    onConfirm={(e) => {
                        e.stopPropagation();
                        setDeleteModalOpen(false);
                        optimisticDeletePreference({
                            id: resourceIdToDelete,
                            localTemplates,
                            setLocalTemplates,
                            refreshFn: refreshPreferences,
                            accountId: userProfile.accountId,
                            ownerId: activeOrganizationAccount
                        });
                    }}
                    onCancel={() => {
                        setDeleteModalOpen(false);
                    }}
                />
            )}
        </FormProvider>
    );
}
